<template>
  <div
    class="mpstore"
    role="dialog"
    aria-modal="true"
    aria-label="몰 상품 미리보기"
    @click.self="emit('close')"
  >
    <div class="mpstore__panel">
      <header class="mpstore__head">
        <div class="mpstore__head-text">
          <h2>몰 미리보기</h2>
          <p>구매자에게 보이는 상세 · 후기 · 문의입니다. 구매·작성은 되지 않습니다.</p>
        </div>
        <div class="mpstore__head-actions">
          <a
            v-if="mallHref"
            class="mpstore__mall-link"
            :href="mallHref"
            target="_blank"
            rel="noopener noreferrer"
          >
            몰에서 열기
          </a>
          <button type="button" class="mpstore__close" aria-label="닫기" @click="emit('close')">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </div>
      </header>

      <div v-if="loading" class="mpstore__state">
        <AppIcon icon="mdi-loading" :size="28" />
        <p>상품을 불러오는 중입니다…</p>
      </div>
      <div v-else-if="error" class="mpstore__state is-error">
        <AppIcon icon="mdi-alert-circle-outline" :size="28" />
        <p>{{ error }}</p>
        <button type="button" class="mpstore__retry" @click="load">다시 시도</button>
      </div>

      <template v-else-if="product">
        <div class="mpstore__scroll">
          <div class="mpstore__top">
            <div class="mpstore__gallery">
              <div class="mpstore__main">
                <img v-if="activeImage" :src="activeImage" :alt="product.name" />
                <div v-else class="mpstore__main-empty">
                  <AppIcon icon="mdi-image-outline" :size="34" />
                  <span>등록된 이미지가 없습니다</span>
                </div>
                <em v-if="product.badge" class="mpstore__badge">{{ product.badge }}</em>
              </div>
              <ul v-if="images.length > 1" class="mpstore__thumbs">
                <li v-for="(image, index) in images" :key="image">
                  <button
                    type="button"
                    :class="{ 'is-active': index === activeIndex }"
                    @click="activeIndex = index"
                  >
                    <img :src="image" :alt="`${product.name} 이미지 ${index + 1}`" />
                  </button>
                </li>
              </ul>
            </div>

            <div class="mpstore__buy">
              <p class="mpstore__store">
                <AppIcon icon="mdi-storefront-outline" :size="15" />
                {{ product.storeName || '스토어' }}
                <span class="mpstore__cat">{{ product.category }}</span>
              </p>

              <h3 class="mpstore__name">{{ product.name }}</h3>
              <p v-if="product.summary" class="mpstore__summary">{{ product.summary }}</p>

              <p class="mpstore__rating">
                <AppIcon icon="mdi-star" :size="16" />
                <strong>{{ product.rating.toFixed(1) }}</strong>
                <span>후기 {{ formatPrice(product.reviewCount) }}</span>
                <span>판매 {{ formatPrice(product.soldCount) }}</span>
              </p>

              <div class="mpstore__price">
                <strong>{{ formatPrice(storefrontOffer(product).price) }}</strong>
                <span v-if="storefrontOffer(product).name">{{ storefrontOffer(product).name }}</span>
                <span>원</span>
              </div>

              <dl class="mpstore__info">
                <div v-if="product.unit">
                  <dt>구성</dt>
                  <dd>{{ product.unit }}</dd>
                </div>
                <div>
                  <dt>배송비</dt>
                  <dd>{{ shippingText }}</dd>
                </div>
                <div v-if="product.leadTime">
                  <dt>출고</dt>
                  <dd>{{ product.leadTime }}</dd>
                </div>
                <div v-if="sameDayCutoffText">
                  <dt>당일 출고</dt>
                  <dd>{{ sameDayCutoffText }}</dd>
                </div>
                <div v-if="isOrderPack(product.orderStep)">
                  <dt>주문</dt>
                  <dd>
                    <MallOrderPackMark
                      size="inline"
                      :order-step="product.orderStep || 1"
                      :sell-unit="product.sellUnit"
                    />
                  </dd>
                </div>
                <div v-else-if="product.minOrder > 1">
                  <dt>최소 주문</dt>
                  <dd>{{ formatMinOrderLabel(product.minOrder, product.sellUnit) }}부터</dd>
                </div>
                <div v-if="product.origin || product.maker">
                  <dt>원산지 · 제조</dt>
                  <dd>{{ [product.origin, product.maker].filter(Boolean).join(' · ') }}</dd>
                </div>
                <div>
                  <dt>재고</dt>
                  <dd :class="{ 'is-warn': (product.stock ?? 0) <= 0 }">
                    {{
                      (product.stock ?? 0) > 0
                        ? formatStockCount(product.stock ?? 0, product.sellUnit)
                        : '재고 없음 (품절 노출)'
                    }}
                  </dd>
                </div>
              </dl>

              <ul v-if="product.options?.length" class="mpstore__options">
                <li v-for="(option, index) in product.options" :key="`${option.group}-${option.name}-${index}`">
                  <span>{{ option.group }} · {{ option.name }}</span>
                  <em v-if="option.extra">+{{ formatPrice(option.extra) }}원</em>
                </li>
              </ul>

              <div class="mpstore__cta">
                <span class="mpstore__cta-buy">바로구매</span>
                <span class="mpstore__cta-wish">찜하기</span>
                <span class="mpstore__cta-cart">장바구니</span>
              </div>
              <p class="mpstore__cta-note">미리보기에서는 구매·작성 버튼이 동작하지 않습니다.</p>
            </div>
          </div>

          <div class="mpstore__tabs" role="tablist" aria-label="상품 정보 탭">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              role="tab"
              class="mpstore__tab"
              :class="{ 'is-active': activeTab === tab.id }"
              :aria-selected="activeTab === tab.id"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
              <em v-if="tab.id === 'review'">{{ product.reviewCount }}</em>
              <em v-else-if="tab.id === 'qna'">{{ (product.qnas || []).length }}</em>
            </button>
          </div>

          <section v-show="activeTab === 'detail'" class="mpstore__section">
            <h4>상품 정보</h4>
            <dl v-if="product.highlights.length" class="mpstore__spec">
              <div v-for="spec in product.highlights" :key="spec.label">
                <dt>{{ spec.label }}</dt>
                <dd>{{ spec.value }}</dd>
              </div>
            </dl>
            <p v-else class="mpstore__empty">등록된 요약 정보가 없습니다.</p>

            <h4>상세 설명</h4>
            <p v-if="product.description" class="mpstore__desc">{{ product.description }}</p>
            <div v-if="blocks.length" class="mpstore__blocks">
              <template v-for="(block, index) in blocks" :key="index">
                <h5 v-if="block.type === 'HEADING'" class="mpstore__block-heading">{{ block.text }}</h5>
                <p v-else-if="block.type === 'TEXT'" class="mpstore__block-text">{{ block.text }}</p>
                <p v-else-if="block.type === 'NOTICE'" class="mpstore__block-notice">
                  <AppIcon icon="mdi-information-outline" :size="16" />
                  {{ block.text }}
                </p>
                <figure v-else class="mpstore__block-image">
                  <img :src="block.imageUrl" :alt="block.alt || product.name" />
                  <figcaption v-if="block.alt">{{ block.alt }}</figcaption>
                </figure>
              </template>
            </div>
            <p v-else-if="!product.description" class="mpstore__empty">상세 설명이 없습니다.</p>
          </section>

          <section v-show="activeTab === 'review'" class="mpstore__section">
            <h4>구매후기 {{ product.reviewCount }}</h4>
            <div v-if="product.reviews.length" class="mpstore__list">
              <article v-for="review in product.reviews" :key="review.id" class="mpstore__card">
                <header>
                  <strong>{{ review.author }}</strong>
                  <span v-if="review.org">{{ review.org }}</span>
                  <em>★ {{ review.rating }}</em>
                  <time>{{ review.date }}</time>
                </header>
                <p>{{ review.content }}</p>
                <img v-if="review.image" :src="review.image" :alt="`${product.name} 후기 사진`" />
                <div v-if="review.reply" class="mpstore__reply">
                  <strong>판매자 답변</strong>
                  <p>{{ review.reply }}</p>
                  <time v-if="review.repliedAt">{{ review.repliedAt }}</time>
                </div>
              </article>
            </div>
            <p v-else class="mpstore__empty">등록된 구매후기가 없습니다.</p>
          </section>

          <section v-show="activeTab === 'qna'" class="mpstore__section">
            <h4>상품문의 {{ (product.qnas || []).length }}</h4>
            <div v-if="(product.qnas || []).length" class="mpstore__list">
              <article v-for="item in product.qnas || []" :key="item.id" class="mpstore__card">
                <header>
                  <strong>{{ item.secret ? '비밀문의' : item.author }}</strong>
                  <span v-if="!item.secret && item.org">{{ item.org }}</span>
                  <time>{{ item.date }}</time>
                </header>
                <p>
                  <strong class="mpstore__q">Q.</strong>
                  <em v-if="item.secret" class="mpstore__secret-tag">비밀</em>
                  {{ item.question }}
                </p>
                <div v-if="item.answer" class="mpstore__reply">
                  <strong>A. 답변</strong>
                  <p>{{ item.answer }}</p>
                  <time v-if="item.answeredAt">{{ item.answeredAt }}</time>
                </div>
                <p v-else class="mpstore__pending">답변 대기 중</p>
              </article>
            </div>
            <p v-else class="mpstore__empty">등록된 상품문의가 없습니다.</p>
          </section>

          <section v-show="activeTab === 'shipping'" class="mpstore__section">
            <h4>배송 · 교환 · 반품</h4>
            <dl class="mpstore__spec">
              <div>
                <dt>배송비</dt>
                <dd>{{ shippingText }}</dd>
              </div>
              <div v-if="product.leadTime">
                <dt>출고</dt>
                <dd>{{ product.leadTime }}</dd>
              </div>
              <div v-if="sameDayCutoffText">
                <dt>당일 출고</dt>
                <dd>{{ sameDayCutoffText }}</dd>
              </div>
              <div v-if="product.sellerCode">
                <dt>반품 배송비</dt>
                <dd>{{ returnShippingText }}</dd>
              </div>
            </dl>
            <p
              v-if="product.storeReturnPolicy"
              class="mpstore__policy-text"
            >{{ product.storeReturnPolicy }}</p>
            <div
              v-else
              class="mpstore__policy"
              v-html="returnPolicyHtml"
            />
          </section>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import MallOrderPackMark from '@/components/mall/MallOrderPackMark.vue'
import { fetchAdminProduct } from '@/api/admin'
import { fetchSellerProduct, toMallProduct } from '@/api/mallProduct'
import { formatPrice, type MallProduct } from '@/constants/mallProducts'
import { useContentSnippets, useSameDayCutoffText, SNIPPET_FALLBACKS } from '@/composables/useContentSnippets'
import { mallProductPath } from '@/constants/mall'
import { formatShippingFeeAmount } from '@/utils/mallShipping'
import { formatMinOrderLabel, formatShippingChargeLabel, formatStockCount, isOrderPack, storefrontOffer } from '@/utils/mallSell'

const props = defineProps<{
  code: string
  audience: 'admin' | 'seller'
  /** 몰 공개 상태일 때만 새 탭 링크를 켠다 */
  mallOpen?: boolean
  /** 열릴 때 바로 보일 탭 (평점→후기 등) */
  initialTab?: 'detail' | 'review' | 'qna' | 'shipping'
}>()

const emit = defineEmits<{ (event: 'close'): void }>()

const { snippet, loadSnippets } = useContentSnippets()

const loading = ref(true)
const error = ref('')
const product = ref<MallProduct | null>(null)
const { sameDayCutoffText } = useSameDayCutoffText(() => product.value?.storeCutoff)
const activeIndex = ref(0)
const activeTab = ref<'detail' | 'review' | 'qna' | 'shipping'>(props.initialTab || 'detail')

watch(
  () => props.initialTab,
  (tab) => {
    if (tab) activeTab.value = tab
  },
)

const tabs = [
  { id: 'detail' as const, label: '상세정보' },
  { id: 'review' as const, label: '구매후기' },
  { id: 'qna' as const, label: '상품문의' },
  { id: 'shipping' as const, label: '배송/교환' },
]

const images = computed(() => {
  const item = product.value
  if (!item) return [] as string[]
  const list = item.images?.length ? item.images : [item.image]
  return list.filter(Boolean)
})

const activeImage = computed(() => images.value[activeIndex.value] ?? images.value[0] ?? '')

const blocks = computed(() => product.value?.blocks ?? [])

const shippingText = computed(() =>
  product.value ? formatShippingChargeLabel(product.value) : formatShippingFeeAmount(0),
)

const returnShippingText = computed(() =>
  formatShippingFeeAmount(product.value?.storeReturnFee),
)

const returnPolicyHtml = computed(
  () => snippet('mall.product.return_policy') || SNIPPET_FALLBACKS['mall.product.return_policy'],
)

const mallHref = computed(() => {
  if (!props.mallOpen || !product.value) return ''
  return mallProductPath(product.value.id)
})

async function load() {
  const code = props.code.trim()
  if (!code) {
    error.value = '상품 코드가 없습니다.'
    loading.value = false
    return
  }

  loading.value = true
  error.value = ''
  product.value = null
  activeIndex.value = 0
  activeTab.value = 'detail'

  try {
    const detail =
      props.audience === 'admin' ? await fetchAdminProduct(code) : await fetchSellerProduct(code)
    product.value = toMallProduct(detail)
  } catch (err) {
    error.value = err instanceof Error ? err.message : '상품을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
  }
}

watch(
  () => [props.code, props.audience] as const,
  () => {
    void load()
  },
)

watch(images, () => {
  activeIndex.value = 0
})

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  void loadSnippets(['mall.product.return_policy', 'mall.product.same_day_cutoff'])
  void load()
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style lang="scss" src="./ManageProductStorefrontModal.scss"></style>
